import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchReportSnapshot } from "@/homeiq/services/reportClient";
import { HOMEIQ_LOGO, VASTU_BY_HOMEIQ_LOGO, VASTU_LOGO } from "@/assets/brand";

// ─────────────────────────────────────────────────────────────────
// HomeIQ Report — 10-page curated executive PDF.
// Playwright renders this route to A4. Every page is `.report-page`,
// CSS sets size: A4 + page-break-after: always.
// ─────────────────────────────────────────────────────────────────

const INR = (n) => {
    if (!Number.isFinite(n)) return "—";
    if (Math.abs(n) >= 10_000_000) return `₹${(n / 10_000_000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100_000) return `₹${(n / 100_000).toFixed(1)} L`;
    if (Math.abs(n) >= 1000) return `₹${Math.round(n).toLocaleString("en-IN")}`;
    return `₹${Math.round(n)}`;
};
const PCT = (n, d = 1) => (Number.isFinite(n) ? `${n.toFixed(d)}%` : "—");
const INT = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString("en-IN") : "—");

const scoreTone = (s) =>
    s >= 80 ? "#16a34a" : s >= 65 ? "#1E6BFF" : s >= 50 ? "#d97706" : "#dc2626";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit", month: "long", year: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

// ─────────── Layout primitives ───────────
function Page({ children, num, title, footerRight }) {
    return (
        <section className="report-page" data-page={num} data-testid={`report-page-${num}`}>
            <div className="report-page-inner">
                <header className="report-page-header">
                    <img src={HOMEIQ_LOGO} alt="HomeIQ" className="report-logo" />
                    <div className="report-page-title">{title}</div>
                </header>
                <div className="report-page-body">{children}</div>
                <footer className="report-page-footer">
                    <div>HomeIQ Advisory Report · Confidential</div>
                    <div>{footerRight ?? `Page ${num} of 10`}</div>
                </footer>
            </div>
        </section>
    );
}

function StatGrid({ stats }) {
    return (
        <div className="stat-grid">
            {stats.map((s) => (
                <div key={s.label} className="stat-card">
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value" style={s.tone ? { color: s.tone } : undefined}>
                        {s.value}
                    </div>
                    {s.sub && <div className="stat-sub">{s.sub}</div>}
                </div>
            ))}
        </div>
    );
}

function Bar({ pct, tone }) {
    const clamped = Math.max(0, Math.min(100, pct));
    return (
        <div className="bar">
            <div className="bar-fill" style={{ width: `${clamped}%`, background: tone || "#1E6BFF" }} />
        </div>
    );
}

function Section({ title, eyebrow, children }) {
    return (
        <div className="section">
            {eyebrow && <div className="eyebrow">— {eyebrow}</div>}
            {title && <h2 className="section-title">{title}</h2>}
            {children}
        </div>
    );
}

// ─────────── Pages ───────────
function P1Cover({ data }) {
    const score = data.homeiq_score ?? 0;
    const tone = scoreTone(score);
    return (
        <section className="report-page cover-page" data-page={1} data-testid="report-page-1">
            <div className="cover-content">
                <img src={HOMEIQ_LOGO} alt="HomeIQ" className="cover-logo" />
                <div className="cover-eyebrow">— HomeIQ Advisory Report</div>
                <h1 className="cover-title">Your home-buying decision, in 10 pages.</h1>
                <div className="cover-score-wrap">
                    <div className="cover-score-ring" style={{ borderColor: tone }}>
                        <div className="cover-score-num" style={{ color: tone }}>{score}</div>
                        <div className="cover-score-of">/100</div>
                    </div>
                    <div className="cover-score-meta">
                        <div className="cover-score-label">HomeIQ Score</div>
                        <div className="cover-score-verdict" style={{ color: tone }}>{data.verdict ?? "—"}</div>
                    </div>
                </div>
                <div className="cover-meta-grid">
                    <div>
                        <div className="cover-meta-label">Prepared for</div>
                        <div className="cover-meta-value">{data.lead?.name ?? "—"}</div>
                    </div>
                    <div>
                        <div className="cover-meta-label">City</div>
                        <div className="cover-meta-value">Bengaluru</div>
                    </div>
                    <div>
                        <div className="cover-meta-label">Date</div>
                        <div className="cover-meta-value">{fmtDate(data.created_at)}</div>
                    </div>
                    <div>
                        <div className="cover-meta-label">Report ID</div>
                        <div className="cover-meta-value mono">{data.id?.slice(0, 13)}…</div>
                    </div>
                </div>
            </div>
            <div className="cover-grid-bg" aria-hidden="true" />
        </section>
    );
}

function P2Summary({ data }) {
    const aff = data.snapshot.affordability;
    const score = data.homeiq_score;
    const verdict = score >= 75 ? "Buy" : score >= 55 ? "Negotiate" : "Hold";
    const verdictTone = scoreTone(score);
    const insights = buildInsights(data.snapshot);
    return (
        <Page num={2} title="Executive Summary">
            <div className="verdict-card">
                <div>
                    <div className="eyebrow">— Verdict</div>
                    <div className="verdict-value" style={{ color: verdictTone }}>{verdict}</div>
                    <div className="verdict-sub">{data.verdict ?? "—"} · HomeIQ Score {score}</div>
                </div>
                <div className="verdict-meta">
                    <StatItem label="Recommended budget" value={INR(aff.max_affordable_property)} />
                    <StatItem label="Eligible EMI" value={INR(aff.emi_on_eligible_loan)} />
                </div>
            </div>

            <Section title="Top 3 insights" eyebrow="What stands out">
                <ul className="insight-list">
                    {insights.map((t, i) => (
                        <li key={i}><strong>{t.title}.</strong> {t.body}</li>
                    ))}
                </ul>
            </Section>

            <Section title="Pillar breakdown" eyebrow="Score composition">
                <div className="pillar-grid">
                    {data.snapshot.homeIQScore.pillars.map((p) => (
                        <div key={p.id} className={`pillar-row ${p.included ? "" : "muted"}`}>
                            <div className="pillar-label">{p.label}</div>
                            <Bar pct={p.included ? p.score : 0} tone={scoreTone(p.score)} />
                            <div className="pillar-score">{p.included ? p.score : "—"}</div>
                        </div>
                    ))}
                </div>
            </Section>
        </Page>
    );
}

function P3Affordability({ data }) {
    const aff = data.snapshot.affordability;
    const inputs = data.snapshot.inputs;
    return (
        <Page num={3} title="Affordability Analysis">
            <Section eyebrow="Snapshot" title={`You can comfortably buy up to ${INR(aff.max_affordable_property)}`}>
                <StatGrid stats={[
                    { label: "Eligible loan", value: INR(aff.eligible_loan) },
                    { label: "Recommended EMI", value: INR(aff.emi_on_eligible_loan) },
                    { label: "FOIR usage", value: PCT(aff.foir_usage_pct, 0), tone: aff.foir_status === "Acceptable" ? "#16a34a" : "#d97706" },
                    { label: "Segment", value: aff.segment },
                ]} />
            </Section>
            <Section eyebrow="Inputs we used" title="Your profile">
                <div className="kv-grid">
                    <KV k="Monthly salary" v={INR(inputs.salary)} />
                    <KV k="Existing obligations" v={INR(inputs.obligations)} />
                    <KV k="Bank FOIR rule" v={`${inputs.bankRule}%`} />
                    <KV k="Down payment" v={INR(inputs.downPayment)} />
                    <KV k="Tenure" v={`${inputs.tenure} years`} />
                    <KV k="Rate" v={`${inputs.rate}%`} />
                </div>
            </Section>
            <Section eyebrow="Stress test" title="If rates moved ±100 bps…">
                <p className="body-text">
                    {aff.stress_dscr
                        ? `Debt-service coverage ratio under stress: ${aff.stress_dscr.toFixed(2)}× — ${aff.stress_dscr >= 1 ? "your headroom holds." : "needs reinforcement."}`
                        : "DSCR stress test runs on the live dashboard — adjust rate slider to re-evaluate."}
                </p>
                {aff.is_alert && (
                    <div className="alert-box">An affordability alert is active. We&apos;ve capped the recommendation accordingly.</div>
                )}
            </Section>
        </Page>
    );
}

function P4Loan({ data }) {
    const lo = data.snapshot.loan;
    return (
        <Page num={4} title="Loan Analysis">
            <Section eyebrow="Loan structure" title={`${INR(lo.principal)} over ${lo.tenure} years at ${lo.rate}%`}>
                <StatGrid stats={[
                    { label: "Total interest", value: INR(lo.total_interest), tone: "#d97706" },
                    { label: "Total outflow", value: INR(lo.total_outflow) },
                    { label: "Interest share", value: PCT(lo.interest_pct, 0) },
                    { label: "EMI / salary", value: PCT(lo.emi_to_salary_pct, 0) },
                ]} />
            </Section>
            {lo.prepayment && (
                <Section eyebrow="Prepayment opportunity" title={`${INR(lo.prepayment.lump)} ${lo.prepayment.frequency.toLowerCase()} could save ${INR(lo.prepayment.interest_saved)}`}>
                    <p className="body-text">
                        Closing <strong>{lo.prepayment.months_saved} months</strong> earlier means new total interest drops to <strong>{INR(lo.prepayment.new_total_interest)}</strong>.
                    </p>
                </Section>
            )}
            <Section eyebrow="Why this matters" title="The hidden cost of waiting">
                <p className="body-text">
                    Every 25 bps of rate movement on this loan size shifts your total interest by approximately {INR(lo.principal * 0.0025 * lo.tenure)} over the full tenure. Lock in early — the next revision is rarely in the borrower&apos;s favour.
                </p>
            </Section>
        </Page>
    );
}

function P5Market({ data }) {
    const mk = data.snapshot.market;
    return (
        <Page num={5} title="Market Intelligence">
            <Section eyebrow="Reachability" title={`${mk.within_count} of ${mk.within_count + mk.beyond_count + (mk.top_markets?.length ? 0 : 0)} verified markets sit within your budget`}>
                <div className="body-text">
                    {mk.within_count > 0
                        ? `Of Bengaluru's 24-market verified dataset, ${mk.within_count} fall comfortably within ${INR(mk.budget)}.`
                        : `No markets within ${INR(mk.budget)} — see Page 7 for the closest stretch option.`}
                </div>
            </Section>
            <Section eyebrow="Top 3 recommended markets" title="Highest CAGR within your budget">
                <table className="market-table">
                    <thead>
                        <tr>
                            <th>Locality</th><th>Zone</th><th>Growth</th><th>Metro</th><th>Property cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(mk.top_markets ?? []).map((m) => (
                            <tr key={m.id}>
                                <td><strong>{m.area}</strong></td>
                                <td>{m.zone}</td>
                                <td style={{ color: scoreTone(m.growth * 6) }}>{PCT(m.growth, 1)}</td>
                                <td className="small">{m.metro?.line ?? "—"}</td>
                                <td>{INR(m.propertyCost)}</td>
                            </tr>
                        ))}
                        {(!mk.top_markets || mk.top_markets.length === 0) && (
                            <tr><td colSpan="5" className="muted">No within-budget markets — increase budget or stretch by 10%.</td></tr>
                        )}
                    </tbody>
                </table>
            </Section>
        </Page>
    );
}

function P6Investment({ data }) {
    const inv = data.snapshot.investment;
    if (inv.blocked) {
        return (
            <Page num={6} title="Investment Analysis">
                <div className="blocked-card">
                    <div className="eyebrow">— Not enough data</div>
                    <div className="blocked-title">Enter your current monthly rent to unlock this analysis.</div>
                    <p className="body-text">
                        The Buy-vs-Rent comparison needs your current rent so we can model the alternative path (rent + invest the down payment). Open the HomeIQ dashboard, enter rent, and regenerate this report.
                    </p>
                </div>
            </Page>
        );
    }
    const buyEdge = inv.buy_equity_at_horizon - inv.rent_invest_equity_at_horizon;
    return (
        <Page num={6} title="Investment Analysis">
            <Section eyebrow="Wealth at horizon" title={`At year ${inv.horizon_years}, buying ${buyEdge >= 0 ? "is ahead by" : "trails by"} ${INR(Math.abs(buyEdge))}`}>
                <StatGrid stats={[
                    { label: `Buy equity (Y${inv.horizon_years})`, value: INR(inv.buy_equity_at_horizon), tone: "#16a34a" },
                    { label: `Rent-invest equity (Y${inv.horizon_years})`, value: INR(inv.rent_invest_equity_at_horizon) },
                    { label: "Break-even year", value: inv.buy_vs_rent_breakeven_year ?? "—" },
                ]} />
            </Section>
            {inv.cost_of_waiting && (
                <Section eyebrow="Cost of waiting" title="What every year of delay costs">
                    <p className="body-text">
                        Delaying purchase by 12 months at current CAGR adds roughly <strong>{INR(inv.cost_of_waiting.delta_12m ?? 0)}</strong> to your entry price.
                    </p>
                </Section>
            )}
            {inv.recommendation && (
                <Section eyebrow="Recommendation" title={inv.recommendation.financial?.verdict ?? "—"}>
                    <p className="body-text">
                        Market verdict: <strong>{inv.recommendation.market?.verdict ?? "—"}</strong>. Lifestyle fit: <strong>{inv.recommendation.lifestyle?.verdict ?? "—"}</strong>.
                    </p>
                </Section>
            )}
        </Page>
    );
}

function P7BestMarket({ data }) {
    const best = data.snapshot.market.best_market;
    if (!best) {
        return (
            <Page num={7} title="Recommended Locality">
                <div className="blocked-card">
                    <div className="blocked-title">No within-budget locality met our growth bar.</div>
                    <p className="body-text">Consider expanding the budget by 10% or relaxing zone preferences.</p>
                </div>
            </Page>
        );
    }
    return (
        <Page num={7} title="Recommended Locality">
            <Section eyebrow="Single best fit" title={best.area}>
                <div className="best-market-card">
                    <div className="best-market-meta">
                        <KV k="Zone" v={best.zone} />
                        <KV k="Projected CAGR" v={PCT(best.growth, 1)} tone="#16a34a" />
                        <KV k="Property cost" v={INR(best.propertyCost)} />
                        {best.metro?.line && <KV k="Metro" v={`${best.metro.line} · ${best.metro.status === "operational" ? "Live" : best.metro.expectedOperational ?? best.metro.status}`} />}
                        {best.roadConnectivity && <KV k="Roads" v={best.roadConnectivity} />}
                    </div>
                </div>
            </Section>
            <Section eyebrow="Why this locality" title="Key growth drivers">
                <ul className="insight-list">
                    {best.context?.itHub && <li><strong>Employment.</strong> Anchored by {best.context.itHub} — sustained rental demand.</li>}
                    {best.metro?.line && <li><strong>Connectivity.</strong> {best.metro.line} {best.metro.status === "operational" ? "is live today" : `arrives ${best.metro.expectedOperational ?? "soon"}`}, compressing commute time.</li>}
                    {best.context?.schools && <li><strong>Liveability.</strong> Schools: {best.context.schools}.</li>}
                    {best.context?.rentalDemand && <li><strong>Rental demand.</strong> {best.context.rentalDemand}.</li>}
                    <li><strong>HomeIQ growth score.</strong> {best.growth_score}/100 against the 24-market Bengaluru benchmark.</li>
                </ul>
            </Section>
        </Page>
    );
}

function P8Vastu({ data }) {
    const v = data.snapshot.vastu;
    if (!v) {
        return (
            <Page num={8} title="Vastu Analysis">
                <div className="blocked-card vastu-blocked">
                    <img src={VASTU_LOGO} alt="Vastu" className="page-logo" />
                    <div className="blocked-title">Vastu read not run on this report.</div>
                    <p className="body-text">
                        Open the Vastu tab in HomeIQ, upload your floor plan (PNG / JPG / PDF) and re-generate the report to include a tradition-aware advisory read on this dwelling.
                    </p>
                    <p className="body-text muted small">
                        Vastu insights are advisory only and are <em>not</em> factored into the HomeIQ Composite Score.
                    </p>
                </div>
            </Page>
        );
    }
    return (
        <Page num={8} title="Vastu Analysis">
            <div className="vastu-header">
                <img src={VASTU_LOGO} alt="Vastu" className="page-logo" />
                <div>
                    <div className="eyebrow">— Vastu Advisory · independent of HomeIQ Score</div>
                    <div className="vastu-score" style={{ color: scoreTone(v.overall_score) }}>
                        {v.overall_score}<span className="small">/100</span>
                    </div>
                    <div className="vastu-verdict">{v.verdict}</div>
                </div>
            </div>
            <Section eyebrow="6-dimension breakdown">
                <div className="pillar-grid">
                    {(v.dimensions ?? []).map((d) => (
                        <div key={d.name} className="pillar-row">
                            <div className="pillar-label">{d.name}</div>
                            <Bar pct={d.score} tone={scoreTone(d.score)} />
                            <div className="pillar-score">{d.score}</div>
                        </div>
                    ))}
                </div>
            </Section>
            {(v.recommendations ?? []).slice(0, 2).length > 0 && (
                <Section eyebrow="Top recommendations">
                    <ul className="insight-list">
                        {v.recommendations.slice(0, 2).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                </Section>
            )}
        </Page>
    );
}

function P9ActionPlan({ data }) {
    const actions = buildActionPlan(data.snapshot);
    return (
        <Page num={9} title="Personalized Action Plan">
            <Section eyebrow="Your next 5 moves" title="Sequenced for impact">
                <ol className="action-list">
                    {actions.map((a, i) => (
                        <li key={i}>
                            <div className="action-title">{a.title}</div>
                            <div className="action-body">{a.body}</div>
                        </li>
                    ))}
                </ol>
            </Section>
        </Page>
    );
}

function P10Contact({ data }) {
    return (
        <Page num={10} title="Contact · Methodology · Disclaimer">
            <Section eyebrow="Talk to a HomeIQ advisor">
                <div className="contact-card">
                    <div className="contact-label">Email</div>
                    <div className="contact-value">advisor@homeiq.in</div>
                    <div className="contact-label">Reply directly</div>
                    <div className="contact-value">Reply to the email this PDF was attached to — a HomeIQ analyst will respond within 24 hours.</div>
                </div>
            </Section>
            <Section eyebrow="Methodology">
                <p className="body-text">
                    HomeIQ Score = weighted average of Affordability, Loan, Market and Investment pillars (Vastu excluded). When a pillar is missing inputs, weights re-normalise so the score remains comparable.
                </p>
                <p className="body-text">
                    Market dataset: HomeIQ Verified Bengaluru Micro-Market Dataset · 24 verified localities. Rates from RBI floating-rate benchmarks (Feb 2026).
                </p>
            </Section>
            <Section eyebrow="Disclaimer">
                <p className="body-text muted small">
                    This report is advisory only and does not constitute financial, legal or investment advice. Estimates are based on the inputs you provided; verify against your bank&apos;s sanction letter and actual builder pricing before any transaction.
                </p>
            </Section>
            <div className="report-id-block">
                <div className="eyebrow">— Report ID</div>
                <div className="mono">{data.id}</div>
                <div className="small muted">Generated {fmtDate(data.created_at)}</div>
            </div>
        </Page>
    );
}

// ─────────── Helpers ───────────
function KV({ k, v, tone }) {
    return (
        <div className="kv-row">
            <div className="kv-key">{k}</div>
            <div className="kv-value" style={tone ? { color: tone } : undefined}>{v}</div>
        </div>
    );
}
function StatItem({ label, value }) {
    return (
        <div className="stat-item">
            <div className="stat-label">{label}</div>
            <div className="stat-value">{value}</div>
        </div>
    );
}

function buildInsights(s) {
    const out = [];
    const aff = s.affordability;
    const lo = s.loan;
    const mk = s.market;
    const inv = s.investment;

    if (aff.foir_status === "Acceptable") {
        out.push({ title: "Your FOIR is healthy", body: `At ${aff.foir_usage_pct.toFixed(0)}% usage you're within the bank's rule — sanction risk is low.` });
    } else {
        out.push({ title: "FOIR is tight", body: `${aff.foir_usage_pct.toFixed(0)}% usage is above the bank rule — consider lowering EMI or extending tenure.` });
    }

    if (mk.within_count >= 3) {
        out.push({ title: `${mk.within_count} markets within budget`, body: `Top growth locality: ${mk.top_markets?.[0]?.area ?? "—"} at ${PCT(mk.top_markets?.[0]?.growth, 1)} CAGR.` });
    } else {
        out.push({ title: "Limited market choice", body: `Only ${mk.within_count} verified Bengaluru market${mk.within_count === 1 ? " sits" : "s sit"} comfortably within ${INR(mk.budget)}.` });
    }

    if (!inv.blocked && inv.buy_vs_rent_breakeven_year) {
        out.push({ title: "Buy beats rent", body: `Break-even at year ${inv.buy_vs_rent_breakeven_year}; by year ${inv.horizon_years} you're ahead by ${INR(Math.max(0, inv.buy_equity_at_horizon - inv.rent_invest_equity_at_horizon))}.` });
    } else if (inv.blocked) {
        out.push({ title: "Rent not entered", body: "Add your current monthly rent on the dashboard to unlock the Buy-vs-Rent comparison." });
    } else {
        out.push({ title: "Rent edge persists", body: "Renting + investing the down payment narrowly outperforms buying at the chosen horizon — revisit when rates ease." });
    }

    if (lo.interest_pct >= 55) {
        out.push({ title: "Interest dominates outflow", body: `${lo.interest_pct}% of total outflow is interest. A modest prepayment unlocks outsized savings.` });
    }

    return out.slice(0, 3);
}

function buildActionPlan(s) {
    const acts = [];
    const aff = s.affordability;
    const lo = s.loan;
    const mk = s.market;
    const inv = s.investment;
    const v = s.vastu;

    acts.push({
        title: `Negotiate within ${INR(aff.max_affordable_property * 0.92)} – ${INR(aff.max_affordable_property)}`,
        body: `Anchor offers 5–8% below your max budget. Walk away above ${INR(aff.max_affordable_property * 1.05)} — that's where DSCR breaks.`,
    });

    if (lo.prepayment) {
        acts.push({
            title: `Pre-pay ${INR(lo.prepayment.lump)} ${lo.prepayment.frequency.toLowerCase()}`,
            body: `Closes the loan ${lo.prepayment.months_saved} months early and saves ${INR(lo.prepayment.interest_saved)} in interest.`,
        });
    } else {
        acts.push({
            title: "Open a prepayment SIP",
            body: `Routing 10% of monthly EMI into a short-term debt fund builds a prepayment corpus in 24 months.`,
        });
    }

    if (mk.top_markets?.length) {
        acts.push({
            title: `Shortlist 3 properties in ${mk.top_markets.map((m) => m.area).join(", ")}`,
            body: "Visit each at different times of day. Cross-check builder RERA status and OC before any token payment.",
        });
    }

    acts.push({
        title: "Lock the loan rate window",
        body: `At ${lo.rate}% you're within mainstream Bengaluru rates. Get sanctioned offers from 3 lenders before negotiating with the builder.`,
    });

    if (v) {
        acts.push({
            title: "Address top Vastu concern",
            body: v.recommendations?.[0] ?? "Review the 6-dimension breakdown on Page 8 with your family before signing.",
        });
    } else if (!inv.blocked) {
        acts.push({
            title: "Run a Vastu read before signing",
            body: "Upload the floor plan in the Vastu tab — tradition-aware advisory, optional but recommended for family alignment.",
        });
    }

    return acts.slice(0, 5);
}

// ─────────── Entry ───────────
export default function ReportPrint() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchReportSnapshot(id)
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setErr(e.message); });
        return () => { cancelled = true; };
    }, [id]);

    // Signal Playwright that the report finished mounting + first paint.
    useEffect(() => {
        if (data) {
            // Two-frame wait so charts / web fonts finish settling.
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    document.body.setAttribute("data-print-ready", "true");
                })
            );
        }
    }, [data]);

    if (err) return <div className="report-loading" data-testid="report-print-error">Error: {err}</div>;
    if (!data) return <div className="report-loading" data-testid="report-print-loading">Loading…</div>;

    return (
        <div className="report-print-root" data-testid="report-print-root">
            <P1Cover data={data} />
            <P2Summary data={data} />
            <P3Affordability data={data} />
            <P4Loan data={data} />
            <P5Market data={data} />
            <P6Investment data={data} />
            <P7BestMarket data={data} />
            <P8Vastu data={data} />
            <P9ActionPlan data={data} />
            <P10Contact data={data} />
        </div>
    );
}
